declare global {
  interface Window {
    Clerk?: {
      loaded?: boolean;
      load?: () => Promise<void>;
      setActive?: (params: { organization: string | null }) => Promise<void>;
      session?: {
        getToken: (options?: {
          skipCache?: boolean;
          template?: string;
        }) => Promise<string | null>;
      } | null;
    };
  }
}

// ── Extension-bridge token override (plugin app surface) ────────────────────
// When this bundle runs as the extension side panel's plugin app
// (plugin.html inside the panel iframe), there is NO Clerk in the page —
// auth arrives from the extension over the postMessage bridge. The bridge
// bootstrap registers itself here and every RTK Query call transparently
// uses the bridged token. Zero effect on the normal portal: nothing
// registers, the branch never triggers.
let bridgeTokenActive = false;
let bridgeToken: string | null = null;
let bridgeRefresh: (() => Promise<string | null>) | null = null;

export const activateBridgeAuth = (
  refresh: () => Promise<string | null>
): void => {
  bridgeTokenActive = true;
  bridgeRefresh = refresh;
};

export const setBridgeToken = (token: string | null): void => {
  bridgeToken = token;
};

let forcedRefresh: Promise<string | null> | null = null;
let clearingOrganization: Promise<void> | null = null;
let organizationRemoved = false;

/**
 * The active org was dropped because Clerk no longer has it for this user
 * (deleted, or they were removed). OnboardingCheck reads it to say so rather
 * than switching workspaces or offering a new one without a word.
 */
export const wasActiveOrganizationRemoved = (): boolean => organizationRemoved;

export const acknowledgeOrganizationRemoved = (): void => {
  organizationRemoved = false;
};

/**
 * Clerk 404s every token scoped to an active org that was deleted, or that
 * the user was removed from. Drop the org rather than surface Clerk's error:
 * OnboardingCheck then reactivates another membership or offers a new
 * workspace.
 */
const getClerkToken = async (
  clerk: NonNullable<Window['Clerk']>,
  options?: { skipCache: boolean }
): Promise<string | null> => {
  try {
    return (await clerk.session?.getToken(options)) ?? null;
  } catch (error) {
    const code =
      (error as { code?: string } | null)?.code ??
      (error as { errors?: { code?: string }[] } | null)?.errors?.[0]?.code;
    // The browser is offline: no token, so the request fails as the network
    // error it is. Thrown, it escaped RTK Query's prepareHeaders as an
    // "unhandled error" console.error on every request (Datadog: 61 in two days).
    if (code === 'clerk_offline') return null;
    if (code !== 'organization_not_found_or_unauthorized' || !clerk.setActive)
      throw error;
    organizationRemoved = true;
    // Every request in flight fails at once; one switch covers them all.
    // A failed switch still answers null: Clerk's raw 404 is never the
    // message a screen should show.
    clearingOrganization ??= clerk
      .setActive({ organization: null })
      .catch(() => undefined)
      .finally(() => {
        clearingOrganization = null;
      });
    await clearingOrganization;
    return null;
  }
};

/**
 * Get a Clerk JWT for authenticated API calls.
 *
 * Pass `forceRefresh: true` to bypass Clerk's in-memory token cache, which is
 * the right thing to do after a 401 - otherwise the retry sees the same stale
 * token Clerk just handed back and 401s again, leaving the user in a refresh
 * loop until they reload the page.
 */
export const fetchWebtokenHeadless = async (options?: {
  forceRefresh?: boolean;
}): Promise<string | null> => {
  if (bridgeTokenActive) {
    if (options?.forceRefresh && bridgeRefresh) return bridgeRefresh();
    return bridgeToken;
  }
  const clerk = window.Clerk;
  if (!clerk) return null;
  if (!clerk.loaded && clerk.load) await clerk.load();
  if (!options?.forceRefresh) return getClerkToken(clerk);
  // A tab back from the background 401s several requests at once; they
  // share one forced refresh instead of each waiting on its own.
  forcedRefresh ??= getClerkToken(clerk, { skipCache: true }).finally(() => {
    forcedRefresh = null;
  });
  return forcedRefresh;
};
